/**
 * Prefetch utilities - Compatibility layer for CacheManager
 * This wrapper allows existing code to work without changes while using the new unified cache
 */
import CacheManager from '../services/CacheManager';
import logger from './logger';

/**
 * Get cached data (compatibility wrapper)
 * @param {string} url - API endpoint
 * @returns {Promise<any>} Cached or fresh data
 */
export const getCachedData = async (url) => {
  return await CacheManager.get(url);
};

/**
 * Prefetch data for a URL
 * @param {string} url - API endpoint to prefetch
 */
export const prefetchData = async (url) => {
  try {
    await CacheManager.get(url);
    logger.debug(`[Prefetch] Prefetched: ${url}`);
  } catch (error) {
    logger.error(`[Prefetch] Error prefetching ${url}:`, error);
  }
};

/**
 * Prefetch common data used across the app
 */
export const prefetchCommonData = async () => {
  logger.info('[Prefetch] Starting common data prefetch');
  
  const urls = [
    '/categories',
    '/banners',
    '/products/by-type/featured',
    '/products/by-type/new_arrivals',
    '/products/by-type/hot_deals'
  ];
  
  await Promise.allSettled(urls.map(url => prefetchData(url)));
  
  logger.info('[Prefetch] Common data prefetch completed');
};

/**
 * Invalidate cache for a URL
 * @param {string} url - API endpoint to invalidate
 */
export const invalidateCache = (url) => {
  CacheManager.invalidate(url);
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  CacheManager.clearAll();
};

/**
 * Subscribe to cache updates (for advanced usage)
 * @param {string} url - API endpoint
 * @param {Function} callback - Called when cache updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToCache = (url, callback) => {
  return CacheManager.subscribe(url, callback);
};

// Default export for backward compatibility
export default {
  getCachedData,
  prefetchData,
  prefetchCommonData,
  invalidateCache,
  clearAllCache,
  subscribeToCache
};
